import { createQueryBuilderV4 } from "@odata2ts/odata-query-builder";
import { useQuery } from "@tanstack/react-query";
import { odataFetch } from "../../lib/odata-fetch";
import {
  WorkOperationType as WorkOperationTypeModel,
  WorkOrderOperation as WorkOrderOperationModel,
} from "../../odata/appu4dm/AppU4DMModel";
import {
  qWorkOrderOperation,
  QWorkOrderOperation,
} from "../../odata/appu4dm/QAppU4DM";
import { useWorkOrderOperationId } from "./use-work-order-operation-id";
import { workOrderKeys } from "./work-order-keys";
import { useMachine } from "../machine/use-machine";

export function useWorkOrderOperation() {
  const { workOrderOperationId, isPending } = useWorkOrderOperationId();
  const { noActiveMachine, machine } = useMachine();

  const { data, ...query } = useQuery({
    queryKey: workOrderKeys.getOperation(
      workOrderOperationId?.WorkOrderOperation_Id,
      machine?.EquipmentNId
    ),
    queryFn: ({ meta }) =>
      fetchWorkOrderOperation(
        workOrderOperationId!.WorkOrderOperation_Id!,
        meta!.token!
      ),
    enabled: ({ meta }) =>
      !!(workOrderOperationId?.WorkOrderOperation_Id && meta?.token),
  });

  return {
    workOrderOperation: data?.value[0],
    noActiveOperation:
      (!query.isFetching && data?.value.length === 0) ||
      noActiveMachine ||
      (!isPending && !workOrderOperationId),
    ...query,
  };
}

type WorkOrderOperation = Omit<WorkOrderOperationModel, "WorkOperationType"> & {
  WorkOperationType: WorkOrderOperationType;
};
type WorkOrderOperationType = Pick<
  WorkOperationTypeModel,
  "NId" | "AM" | "CNC" | "AutoStart" | "AutoComplete"
>;

async function fetchWorkOrderOperation(
  workOrderOperationId: string,
  token: string
) {
  const builder = createQueryBuilderV4(
    "WorkOrderOperation",
    new QWorkOrderOperation()
  )
    .filter(qWorkOrderOperation.Id.eq(workOrderOperationId))
    .expanding("WorkOperationType", (b) =>
      b.select("NId", "AM", "CNC", "AutoStart", "AutoComplete")
    );

  return odataFetch<WorkOrderOperation>({ builder, token });
}
